// Capa TENS (SPEC 3, v1). Dominio puro: catálogos calcados de la plantilla
// oficial de evolución TENS UPCA (docs/plantilla-tens-upca-2026-07-26.md) y
// la guarda de rol. El TENS registra lo que observa y valora, lo que hace y a
// quién avisa. Su documento es la hoja del TENS, no la evolución por sistemas
// de la enfermera, y por eso MiPAE en turno le abre esta capa en vez de la
// ficha del enfermero: esa frontera es del DOCUMENTO y vive acá EN CÓDIGO
// (fijada por test), no en un prompt ni en un límite sobre la persona.
// (Decisión del fundador 2026-10-04: "el TENS sí valora"; hasta entonces esta
// cabecera decía lo contrario y el copy de las pantallas lo repetía.)
//
// Principios no negociables de la capa (dictado del fundador, 26-jul-2026):
// SIEMPRE muy educativa (cada opción explica el porqué en simple, jamás
// acusa), catálogo cerrado sin texto libre en el camino principal, la cama es
// el ancla (jamás identificador de paciente), y un "no realizable" con motivo
// es una decisión correcta documentada, no una falta.

// Única dependencia de este archivo: el catálogo de categorías de LPP, una hoja
// que solo importa segmentosExamen (sin ciclos). Se importa en vez de copiarse
// porque la categoría que declara el TENS tiene que ser LA MISMA de la
// enfermera (mismo vocabulario) y se re-valida al leer en la prosa.
import { CATEGORIAS_LPP } from './pielLpp';
import { CAMPOS_SALA_RESPALDO, MOTIVOS_SALA_EXTRA } from './tensCatalogoSala';

export function esTens(profile) {
  return profile?.rol === 'tens';
}

// ── Frontera de competencias (SPEC 3.2, revisada por el fundador 26-jul) ─────
// Decisión revisada el mismo día: el TENS tiene ACCESO ABIERTO a toda la app
// (escalas, fármacos, enciclopedia, PAE como estudio: es contenido educativo
// y lo usan harto). La frontera clínica que queda EN CÓDIGO es una sola y es
// la que importa: su REGISTRO DEL TURNO es la capa TENS (cuidados delegados,
// observación descriptiva), nunca la ficha del enfermero. App.jsx enruta
// "MiPAE en turno" por rol con esta función; fijado por test.
export function miTurnoEsCapaTens(profile) {
  return esTens(profile);
}

// ── Camas de la unidad piloto (UCI Bupa: 213 a 233) ──────────────────────────
// Desde el plan del mundo TENS (2026-10-04) esta lista ya no es "las camas del
// TENS": es el pizarrón de UN perfil institucional (lib/tensPerfil.js lo
// re-exporta como CAMAS_BUPA_UPCA y solo lo muestra a quien tiene Clínica Bupa
// en su perfil). El resto escribe el número de sus camas. Vive acá porque este
// archivo no importa nada y dos pruebas la importan de aquí.
export const CAMAS_UPCA = Array.from({ length: 21 }, (_, i) => String(213 + i));

// ── Cambios de posición (la función principal, SPEC 3.3) ─────────────────────
export const POSICIONES = [
  { id: 'supino', label: 'Supino' },
  { id: 'lateral_izq', label: 'Lateral izquierdo' },
  { id: 'lateral_der', label: 'Lateral derecho' },
  { id: 'semi_fowler', label: 'Semi Fowler' },
  { id: 'prono', label: 'Prono' },
];

export const DESCARGA_ZONAS = [
  { id: 'talones', label: 'Talones' },
  { id: 'sacro', label: 'Sacro' },
  { id: 'occipucio', label: 'Occipucio' },
  { id: 'almohadillado', label: 'Almohadillado' },
];

// "No realizable" SIEMPRE con motivo, y cada motivo trae su advertencia
// EDUCATIVA: qué sí se puede hacer aunque no se pueda girar. El motivo
// explica, la advertencia enseña. Catálogo inicial dictado por el fundador.
export const MOTIVOS_NO_REALIZABLE = [
  {
    id: 'inestabilidad_hemodinamica',
    label: 'Indicación médica: inestabilidad hemodinámica',
    advertencia: 'Aunque no se pueda girar, protege igual: talones en el aire y alivio del sacro. Cada punto que descargas es piel que se salva.',
  },
  {
    id: 'inestabilidad_respiratoria',
    label: 'Indicación médica: inestabilidad respiratoria',
    advertencia: 'Aunque no se pueda girar, libera zonas de presión: talones, sacro, occipucio. La descarga parcial también previene lesiones.',
  },
  {
    id: 'procedimiento_en_curso',
    label: 'Procedimiento en curso',
    advertencia: 'Cuando termine el procedimiento, retoma el cambio según lo que planificó tu enfermera.',
  },
  {
    id: 'negativa_paciente',
    label: 'El paciente no quiere en este momento',
    // La etiqueta es una frase entera (un chip); dentro de "no realizado por …" no se lee.
    prosa: 'negativa del paciente',
    advertencia: 'Su decisión se respeta y se registra. Ofrece de nuevo en un rato y avisa a tu enfermera si se repite.',
  },
];

// ── Piel al movilizar (SPEC 3.4, el corazón de la capa) ──────────────────────
// Primero lo que se VE, en palabras descriptivas; y, si quien registra quiere,
// la categoría de la lesión con el vocabulario de la enfermera (CATEGORIAS_LPP,
// opcional, nunca condicionada a nada). Toda marca distinta de "sin alteración"
// es una pesquisa que se avisa. `prosa` es cómo se dice dentro de la oración
// cuando la etiqueta del chip no encaja ("Con lesión visible" no es una frase).
export const OBSERVACIONES_PIEL_TENS = [
  { id: 'sin_alteracion', label: 'Sin alteración a la vista', reporta: false },
  { id: 'enrojecida', label: 'Zona enrojecida', reporta: true },
  { id: 'lesion', label: 'Con lesión visible', prosa: 'lesión visible', reporta: true },
  { id: 'humedad', label: 'Humedad', reporta: true },
  { id: 'bajo_dispositivo', label: 'Zona bajo dispositivo', reporta: true },
  { id: 'dolor_referido', label: 'El paciente refiere dolor', reporta: true },
  { id: 'otro', label: 'Otro hallazgo', reporta: true },
];

// ── Cuidados ejecutados (SPEC 3.5, checklist de la plantilla UPCA) ───────────
export const CUIDADOS_TENS = [
  { id: 'aseo_parcial', label: 'Aseo parcial' },
  { id: 'aseo_completo', label: 'Aseo completo en cama' },
  { id: 'aseo_genital', label: 'Aseo genital' },
  { id: 'aseo_bucal', label: 'Aseo bucal' },
  { id: 'ropa_cama', label: 'Cambio de ropa de cama' },
  { id: 'lubricacion', label: 'Lubricación de la piel' },
  { id: 'proteccion_prominencias', label: 'Protección de prominencias' },
  { id: 'manejo_humedad', label: 'Manejo de humedad e incontinencia' },
  { id: 'rotacion_dispositivo', label: 'Rotación de dispositivo' },
];

// Frecuencias de rotación por dispositivo (minutos). Solo lo dictado: CUP
// cada 2 h. Las demás llegan del instructivo; no inventar.
export const ROTACION_DISPOSITIVOS_MIN = { cup: 120 };

// Tolerancia PROVISIONAL de "en ventana" para el cambio de posición (±30 min)
// mientras el fundador dicta la del instructivo. Marcada provisional a
// propósito: cambiarla solo actualiza esta constante y sube spec_version.
export const TOLERANCIA_VENTANA_MIN = 30;

export function enVentana(deltaMin, tolerancia = TOLERANCIA_VENTANA_MIN) {
  if (deltaMin == null) return null;
  return Math.abs(deltaMin) <= tolerancia;
}

// ── Evolución TENS por turno (dictado del fundador 26-jul, segunda vuelta) ───
// El TENS también evoluciona una vez por turno, como el enfermero, con SU
// ventana de oportunidad: la visita/evolución debe quedar antes de las 14:00,
// es decir dentro de las PRIMERAS 6 HORAS del turno (08:00 + 6 h = 14:00).
// La estructura es la plantilla oficial UPCA de Tania, campo por campo:
// catálogo cerrado, cero texto libre, y compilarEvolucionTens escribe la
// prosa EXACTA de la plantilla con lo seleccionado (misma función para
// preview y documento: no reintroducir textos paralelos).
export const VENTANA_EVOLUCION_TENS_MIN = 360;

// Turnos como los nombra la plantilla (Diurno/Nocturno), mapeados al reloj
// de la rotativa (turnoEstudio usa Largo/Noche). La pantalla ya no lo usa: elige
// entre los TURNOS de la app (Largo, Noche, 24 horas) con el mismo nombre que el
// resto de MiPAE en turno. Se conserva por la métrica del estudio y por tests.
export const TURNOS_TENS = [
  { id: 'Largo', label: 'Diurno' },
  { id: 'Noche', label: 'Nocturno' },
];

export const EVOLUCION_TENS = [
  { id: 'condiciones', label: 'Condiciones generales', tipo: 'uno', opciones: [
    { id: 'estables', label: 'Estables' }, { id: 'regulares', label: 'Regulares' }, { id: 'criticas', label: 'Críticas' },
  ]},
  { id: 'conciencia', label: 'Conciencia', tipo: 'uno', opciones: [
    { id: 'vigil', label: 'Vigil' }, { id: 'somnoliento', label: 'Somnoliento' }, { id: 'sedado', label: 'Sedado' }, { id: 'desorientado', label: 'Desorientado' },
  ]},
  { id: 'conducta', label: 'Conducta', tipo: 'uno', opciones: [
    { id: 'tranquilo', label: 'Tranquilo' }, { id: 'inquieto', label: 'Inquieto' }, { id: 'colaborador', label: 'Colaborador' }, { id: 'poco_colaborador', label: 'Poco colaborador' },
  ]},
  { id: 'soporte', label: 'En unidad con', tipo: 'multi', opciones: [
    { id: 'monitorizacion', label: 'monitorización continua' }, { id: 'oxigenoterapia', label: 'oxigenoterapia' },
    { id: 'vm', label: 'ventilación mecánica' }, { id: 'canula_nasal', label: 'cánula nasal' },
    { id: 'mascara_venturi', label: 'máscara Venturi' }, { id: 'aire_ambiente', label: 'aire ambiente' },
  ]},
  { id: 'dva', label: 'Destaca uso de drogas vasoactivas (DVA)', tipo: 'uno', opciones: [
    { id: 'si', label: 'Sí' }, { id: 'no', label: 'No' },
  ]},
  { id: 'piel', label: 'Piel', tipo: 'multi', opciones: [
    { id: 'integra', label: 'íntegra' }, { id: 'hidratada', label: 'hidratada' },
    { id: 'con_lesion', label: 'con lesión' }, { id: 'con_eritema', label: 'con eritema' },
    { id: 'con_proteccion', label: 'con protección cutánea' },
  ]},
  { id: 'dispositivos', label: 'Dispositivos', tipo: 'multi', opciones: [
    { id: 'cvc', label: 'CVC' }, { id: 'cup', label: 'CUP' }, { id: 'sng', label: 'SNG' }, { id: 'sny', label: 'SNY' },
    { id: 'tqt', label: 'TQT' }, { id: 'tet', label: 'TET' }, { id: 'linea_arterial', label: 'línea arterial' },
    { id: 'drenajes', label: 'drenajes' }, { id: 'ostomias', label: 'ostomías' },
  ]},
  { id: 'sitio_insercion', label: 'Sitio de inserción', tipo: 'uno', opciones: [
    { id: 'sin', label: 'Sin signos de alteración' }, { id: 'con', label: 'Con signos de alteración' },
  ]},
  { id: 'tolerancia', label: 'Alimentación', tipo: 'uno', opciones: [
    { id: 'con', label: 'Con tolerancia' }, { id: 'sin', label: 'Sin tolerancia' },
  ]},
  { id: 'regimen', label: 'Régimen', tipo: 'uno', opciones: [
    { id: 'oral', label: 'régimen oral' }, { id: 'enteral', label: 'nutrición enteral' }, { id: 'ayuno', label: 'ayuno' },
  ]},
  { id: 'diuresis', label: 'Diuresis', tipo: 'uno', opciones: [
    { id: 'presente', label: 'presente' }, { id: 'espontanea', label: 'espontánea' },
    { id: 'por_cup', label: 'por CUP' }, { id: 'ausente', label: 'ausente' },
  ]},
  { id: 'deposiciones', label: 'Deposiciones', tipo: 'uno', opciones: [
    { id: 'si', label: 'Sí' }, { id: 'no', label: 'No' },
  ]},
  { id: 'seguridad', label: 'Movilización y seguridad', tipo: 'multi', opciones: [
    { id: 'prevencion_caidas', label: 'prevención de caídas' }, { id: 'barandas', label: 'barandas en alto' },
    { id: 'unidad_ordenada', label: 'unidad ordenada' }, { id: 'brazalete', label: 'brazalete de identificación verificado' },
  ]},
  { id: 'pertenencias', label: 'Pertenencias', tipo: 'uno', opciones: [
    { id: 'completas', label: 'completas' }, { id: 'incompletas', label: 'incompletas' },
  ]},
  { id: 'pertenencias_gestion', label: 'Gestión de pertenencias', tipo: 'uno', opciones: [
    { id: 'resguardadas', label: 'resguardadas' }, { id: 'entregadas', label: 'entregadas a familiar' },
  ]},
  { id: 'educacion_a', label: 'Educación a', tipo: 'uno', opciones: [
    { id: 'paciente', label: 'paciente' }, { id: 'familiar', label: 'familiar' },
  ]},
  { id: 'educacion_tema', label: 'Tema de educación', tipo: 'multi', opciones: [
    { id: 'higiene', label: 'higiene' }, { id: 'prevencion_lpp', label: 'prevención de LPP' },
    { id: 'dispositivos', label: 'manejo de dispositivos' }, { id: 'seguridad', label: 'seguridad' },
  ]},
  { id: 'educacion_resultado', label: 'Resultado', tipo: 'uno', opciones: [
    { id: 'comprende', label: 'Comprende' }, { id: 'no_comprende', label: 'No comprende' }, { id: 'requiere_refuerzo', label: 'Requiere refuerzo' },
  ]},
  { id: 'observacion', label: 'Observaciones', tipo: 'uno', opciones: [
    { id: 'sin_incidentes', label: 'Sin incidentes durante el turno' },
    { id: 'refuerza_cuidados', label: 'Se refuerzan cuidados y vigilancia' },
    { id: 'asiste_examen', label: 'Asiste a examen' },
  ]},
];

// Busca la etiqueta en los campos de la plantilla del servicio y, si no está
// (un valor guardado con otra plantilla: nada registrado se esconde al cambiar
// de servicio), en los de UCI de siempre. Con la plantilla de UCI el primer
// lookup es idéntico al de antes.
const opLabelEn = (campos, campoId, opId) => {
  const buscar = (lista) => lista.find((c) => c.id === campoId)?.opciones.find((o) => o.id === opId)?.label || null;
  // Primero la plantilla del turno, luego la de UCI y por último lo propio de sala:
  // así lo ya registrado se sigue leyendo aunque el servicio cambie a mitad de turno.
  return buscar(campos) || buscar(EVOLUCION_TENS) || buscar(CAMPOS_SALA_RESPALDO);
};

// Un motivo de "no realizable" por su id, sea de UCI o propio de sala: nada
// registrado se esconde porque el servicio del turno cambió.
export const motivoDe = (id) => [...MOTIVOS_NO_REALIZABLE, ...MOTIVOS_SALA_EXTRA].find((m) => m.id === id) || null;

// "CVC, CUP y SNG" (coma + "y" final, como habla la unidad). La "y" se vuelve
// "e" ante palabra que empieza con i o hi, que es castellano correcto y lo que
// una enfermera escribiría: "prevención de LPP e higiene", no "y higiene".
// Excepción de la regla: hie/hia conservan la "y" ("agua y hielo").
// Exportada: la visita técnica (visitaTecnica.js) habla con la MISMA regla.
export function listaProsa(items) {
  if (!items.length) return '';
  if (items.length === 1) return items[0];
  const ultimo = items[items.length - 1];
  const sinTilde = ultimo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const conjuncion = /^(i(?!$)|hi)/.test(sinTilde) && !/^hi[ae]/.test(sinTilde) ? 'e' : 'y';
  return `${items.slice(0, -1).join(', ')} ${conjuncion} ${ultimo}`;
}

// Compila la evolución TENS a la prosa de la plantilla UPCA. Determinista:
// SOLO lo seleccionado o registrado aparece (nada se afirma sin constatar,
// misma invariante que el examen del enfermero). Recibe ademas lo REGISTRADO
// durante el turno (eventos y cuidados de la ficha) y lo integra solo: la
// evolución se arma de lo que ya se hizo, no se re-escribe.
// ¿Qué grupos de la evolución ya vienen cubiertos por la visita? La UI los
// marca en vez de volver a preguntarlos, y el compilador no los duplica.
export function gruposCubiertosPorVisita(visita = {}) {
  const v = visita || {};
  return {
    dispositivos: Object.keys(v.dispositivos || {}).length > 0,
    // Un punto desmarcado en una versión anterior quedaba en null: no cuenta como revisado.
    piel: Object.values(v.piel || {}).some(Boolean),
    alimentacion: !!v.alimentacion?.via,
    pertenencias: !!v.pertenencias?.pertenencias,
    educacion: !!v.educacion?.educacion_a && (v.educacion?.educacion_tema || []).length > 0,
  };
}

// `plantilla` (opcional) trae los campos del servicio; sin ella, los de UCI.
// `riesgoNota` es la frase de catálogo del puntaje de Braden que escribió quien
// registra (la arma tensRegistro, que sí puede importar la escala); sin puntaje
// no hay nota y la frase de frecuencia dice lo mismo que siempre.
// `signos` ([{ hora, texto, avisado }], el texto ya armado y validado por quien llama)
// y `riesgoCaidas` ('alto' | 'bajo') SOLO se cuentan con la plantilla de sala: la
// hoja de UCI de la unidad piloto no lleva signos vitales y su salida es byte a byte
// la de siempre, estén o no esos datos. Todo lo demás que es propio de sala (cuidados
// de la ronda, el campo "Avisé a la enfermera de", los motivos) se cuenta siempre que
// esté registrado: son datos que solo existen si alguien los marcó en sala.
// `visitaConEnfermera` (true por defecto, como siempre) es el rasgo de la UCI de Bupa: con false la
// visita no se cuenta "junto a la enfermera" (ver seccionesVisitaTens).
export function compilarEvolucionTens(valores = {}, {
  eventos = [], cuidados = {}, pielZona = null, visita = null, riesgo = null,
  plantilla = null, riesgoNota = null, signos = [], riesgoCaidas = null, visitaConEnfermera = true,
} = {}) {
  const campos = plantilla?.campos || EVOLUCION_TENS;
  const opLabel = (campoId, opId) => opLabelEn(campos, campoId, opId);
  const esSala = plantilla?.id === 'sala';
  const horaOk = (h) => typeof h === 'string' && /^\d{1,2}:\d{2}$/.test(h);
  // Un valor guardado que no es una lista, o un id que no es de ningún catálogo (un jsonb
  // adulterado, o un registro de otra versión), no imprime "null" ni rompe el render.
  const lista = (v) => (Array.isArray(v) ? v : []);
  const etiquetas = (campoId, ids) => lista(ids).map((i) => opLabel(campoId, i)).filter(Boolean);
  const p = [];
  // TODO lo registrado antes ESCRIBE la evolución y no se vuelve a preguntar:
  // el recibo del turno, la visita con la enfermera, las rondas y el riesgo.
  // La evolución es el destino natural del turno, no un formulario aparte.
  const v = visita || {};
  const sec = seccionesVisitaTens(v, { conEnfermera: visitaConEnfermera !== false });
  const visitaCubre = gruposCubiertosPorVisita(v);

  // Estado general (una oración, como abre la plantilla).
  const estado = [];
  if (valores.condiciones) estado.push(`en ${opLabel('condiciones', valores.condiciones).toLowerCase()} condiciones generales`);
  if (valores.conciencia) estado.push(opLabel('conciencia', valores.conciencia).toLowerCase());
  if (valores.conducta) estado.push(opLabel('conducta', valores.conducta).toLowerCase());
  if (estado.length) p.push(`Paciente ${estado.join(', ')}.`);
  const soporte = etiquetas('soporte', valores.soporte);
  // En sala no se dice "en unidad" (es una cama de UCI, 2026-10-10).
  if (soporte.length) p.push(`${esSala ? 'Se mantiene con' : 'Se encuentra en unidad con'} ${listaProsa(soporte)}.`);
  if (valores.dva === 'si') p.push('Destaca uso de drogas vasoactivas (DVA).');

  // Signos vitales de las rondas (solo sala). El texto ya viene validado; el aviso se
  // constata igual que en todo lo demás: solo se dice "se informa" si se marcó.
  const tomas = esSala
    ? (signos || []).filter((t) => t && typeof t.texto === 'string' && t.texto.trim())
    : [];
  if (tomas.length) {
    p.push(`Signos vitales: ${tomas.map((t) => `${horaOk(t.hora) ? `${t.hora} ` : ''}${t.texto}${t.avisado ? ', se informa a enfermera' : ''}`).join('; ')}.`);
  }

  // El RECIBO del turno NO entra al documento (decisión del fundador,
  // 27-jul): recibir el turno, coordinar con la enfermera y mirar el kárdex
  // son preparación del propio TENS, no hechos clínicos del paciente. La
  // ficha los mantiene como checklist personal y ahí se quedan.

  p.push(...sec.clinicas);

  // Higiene y confort: nace del CHECKLIST de cuidados de la ficha (lo que ya
  // se tocó durante el turno), sin volver a preguntarlo.
  const higieneIds = ['aseo_parcial', 'aseo_completo', 'aseo_genital', 'aseo_bucal', 'ropa_cama', 'lubricacion'];
  const higiene = higieneIds.filter((id) => cuidados[id]).map((id) => CUIDADOS_TENS.find((c) => c.id === id)?.label.toLowerCase());
  if (higiene.length) p.push(`Se realizan cuidados de higiene y confort: ${listaProsa(higiene)}.`);

  // Cambios de posición registrados: la cronología entra sola, con hora.
  // Solo una posición del catálogo entra a la prosa: una que no lo es (un jsonb
  // adulterado) se salta, igual que un motivo o una observación desconocidos.
  const cambios = eventos.filter((e) => e.tipo === 'cambio' && POSICIONES.some((x) => x.id === e.posicion));
  if (cambios.length) {
    const detalle = cambios.map((c) => `${c.hora} ${POSICIONES.find((x) => x.id === c.posicion).label.toLowerCase()}`);
    p.push(`Se asiste en cambios de posición y prevención de lesiones por presión: ${detalle.join(', ')}.`);
  }
  const noRealizados = eventos.filter((e) => e.tipo === 'cambio_no_realizable');
  for (const n of noRealizados) {
    const m = motivoDe(n.motivo);
    if (m) p.push(`Cambio de posición${horaOk(n.hora) ? ` de las ${n.hora}` : ''} no realizado por ${m.prosa || bajaInicial(m.label)}.`);
  }
  // Movilización que se asistió en la ronda (sala). La prosa NO dice "según indicación":
  // nadie registra la indicación y escribirla sería afirmar algo no constatado (revisión
  // clínica del 4-oct). Las fuentes dicen que el técnico realiza lo que define la enfermera.
  const movilizacion = [['levantada_sillon', 'levantada a sillón'], ['deambulacion_asistida', 'deambulación asistida']]
    .filter(([id]) => cuidados[id])
    .map(([id, txt]) => `${txt}${horaOk(cuidados[id]) ? ` a las ${cuidados[id]}` : ''}`);
  if (movilizacion.length) p.push(`Se asiste en movilización: ${listaProsa(movilizacion)}.`);
  // El riesgo de LPP explica la frecuencia: es lo que Calidad audita junto a
  // los cambios, y hasta ahora no llegaba a la evolución.
  const rdef = riesgoDef(riesgo);
  if (rdef) {
    if (riesgoNota) p.push(`${riesgoNota}.`);
    // La frecuencia la define la enfermera (2026-10-10): ni en UCI ni en sala la prosa
    // imprime "cada 2 horas". Antes la UCI lo decía porque era el protocolo de la unidad
    // piloto; el fundador sacó ese reloj para todos.
    p.push(rdef.id === 'bajo'
      ? 'Paciente con riesgo bajo de lesión por presión, se estimula el cambio de posición autónomo.'
      : `Paciente con riesgo ${bajaInicial(rdef.label)} de lesión por presión, se mantienen cambios de posición según la planificación de enfermería.`);
  }
  // Rotación de CUP: registrada en la ronda, entra con sus horas.
  const rotaciones = eventos.filter((e) => e.tipo === 'rotacion_cup');
  if (rotaciones.length) {
    p.push(`Se realiza rotación de CUP: ${rotaciones.map((r) => r.hora).join(', ')}.`);
  }

  // Piel: lo seleccionado, más la zona si la selección la pide.
  if (!visitaCubre.piel && lista(valores.piel).length) {
    const conZona = ['con_lesion', 'con_eritema'];
    const partes = lista(valores.piel).map((id) => {
      const label = opLabel('piel', id);
      return label && conZona.includes(id) && pielZona ? `${label} en ${pielZona.toLowerCase()}` : label;
    }).filter(Boolean);
    if (partes.length) p.push(`Piel ${listaProsa(partes)}.`);
  }
  // Pesquisas del turno: la observación descriptiva ya reportada, con hora.
  const pesquisas = eventos.filter((e) => e.tipo === 'piel' && e.reporta);
  // El aviso se CONSTATA, no se supone: "se informa a enfermera" solo si no
  // quedó explícitamente sin avisar; con `avisada === false` dice "por informar a
  // enfermera", como el brazalete y el sitio de inserción (2026-10-10, antes callaba).
  // Una clave ausente dice lo de siempre, así los registros viejos y el fixture no
  // cambian; todo registro nuevo escribe la clave. La categoría se re-valida contra el
  // catálogo al leer: un valor adulterado jamás se imprime.
  for (const q of pesquisas) {
    const o = OBSERVACIONES_PIEL_TENS.find((x) => x.id === q.obs);
    if (!o) continue;
    const zona = q.zonaLabel ? ` en ${q.zonaLabel.toLowerCase()}` : '';
    const categoria = CATEGORIAS_LPP.includes(q.categoria) ? `, ${bajaInicial(q.categoria)}` : '';
    const aviso = q.avisada === false ? ', por informar a enfermera' : ', se informa a enfermera';
    p.push(`A las ${q.hora} se pesquisa ${o.prosa || o.label.toLowerCase()}${zona}${categoria}${aviso}.`);
  }

  const dispositivos = etiquetas('dispositivos', valores.dispositivos);
  if (!visitaCubre.dispositivos && dispositivos.length) {
    const avisoSitio = `${valores.sitioAvisada === false ? 'por informar' : 'se informa'} a enfermera`;
    if (esSala) {
      // En sala (VVP con su flebitis, drenajes con su permeabilidad) marcar solo el
      // dispositivo no afirma que esté permeable ni fijado: la frase de normalidad es la
      // de la plantilla de UCI. Del sitio de inserción se dice solo lo que se contestó.
      p.push(`Dispositivos: ${listaProsa(dispositivos)}.`);
      if (valores.sitio_insercion === 'con') p.push(`Sitio de inserción con signos de alteración, ${avisoSitio}.`);
      else if (valores.sitio_insercion === 'sin') p.push('Sitio de inserción sin signos de alteración.');
    } else {
      const sitio = valores.sitio_insercion === 'con'
        ? `con signos de alteración en sitio de inserción, ${avisoSitio}`
        : 'sin signos de alteración en sitio de inserción';
      p.push(`Dispositivos: ${listaProsa(dispositivos)} permeables, instalados y fijados, sin tracciones visibles, ${sitio}.`);
    }
  }

  if (visitaCubre.alimentacion) {
    p.push(...sec.alimentacion);
  } else if (valores.regimen === 'ayuno') {
    p.push('Paciente en ayuno según indicación.');
  } else if (valores.tolerancia && valores.regimen) {
    p.push(`Paciente ${opLabel('tolerancia', valores.tolerancia).toLowerCase()} a ${opLabel('regimen', valores.regimen)}. Se asiste en alimentación según indicación y tolerancia.`);
  }

  // La asistencia que se marcó en la ronda se cuenta una sola vez: si el régimen y la
  // tolerancia (o la visita) ya dicen que se asiste según indicación, no se repite. Va con
  // su hora: con el régimen en ayuno (que puede haber cambiado después) la frase sigue
  // siendo cierta, pasó a esa hora.
  if (cuidados.asistencia_alimentacion && !visitaCubre.alimentacion
    && !(valores.regimen && valores.regimen !== 'ayuno' && valores.tolerancia)) {
    p.push(`Se asiste en la alimentación${horaOk(cuidados.asistencia_alimentacion) ? ` a las ${cuidados.asistencia_alimentacion}` : ''}.`);
  }

  if (valores.diuresis) p.push(`Diuresis ${opLabel('diuresis', valores.diuresis)}.`);
  if (valores.deposiciones) p.push(valores.deposiciones === 'si' ? 'Deposiciones presentes en el turno.' : 'Sin deposiciones en el turno.');
  // Mediciones de la ronda (sala). El verbo de las fuentes es "colaborar" (en el registro
  // del balance hídrico, en la medición de drenajes): jamás se interpreta un débito ni se
  // pone un umbral.
  const mediciones = [['medicion_diuresis', 'el registro de diuresis'], ['medicion_drenajes', 'la medición de drenajes']]
    .filter(([id]) => cuidados[id]).map(([, t]) => t);
  if (mediciones.length) p.push(`Se colabora en ${mediciones.join(' y en ')}.`);
  if (cuidados.toma_muestra) p.push('Se toma muestra para examen.');

  // Con riesgo de caídas declarado (solo sala), las medidas de prevención que se
  // marcaron se cuentan bajo ese riesgo; lo demás de "seguridad" (la unidad ordenada,
  // el brazalete) se dice aparte. Sin riesgo, o con la plantilla de UCI, es la frase de
  // siempre con todo junto. `filter(Boolean)`: un id que no es de ningún catálogo no se
  // imprime (antes salía "null").
  const segIds = lista(valores.seguridad);
  const medidasCaidas = esSala ? (plantilla.medidasCaidas || []) : [];
  const conRiesgoCaidas = esSala && (riesgoCaidas === 'alto' || riesgoCaidas === 'bajo');
  const deCaidas = conRiesgoCaidas ? segIds.filter((id) => medidasCaidas.includes(id)) : [];
  if (conRiesgoCaidas) {
    const lista = deCaidas.map((id) => opLabel('seguridad', id)).filter(Boolean);
    p.push(lista.length
      ? `Paciente con riesgo ${riesgoCaidas} de caídas, se mantienen medidas de prevención: ${listaProsa(lista)}.`
      : `Paciente con riesgo ${riesgoCaidas} de caídas.`);
  }
  const restoSeg = segIds.filter((id) => !deCaidas.includes(id)).map((s) => opLabel('seguridad', s)).filter(Boolean);
  if (restoSeg.length) p.push(`Se mantienen medidas de seguridad: ${listaProsa(restoSeg)}.`);

  if (visitaCubre.pertenencias) {
    p.push(...sec.pertenencias);
  } else if (valores.pertenencias) {
    const gestion = valores.pertenencias_gestion ? `, ${opLabel('pertenencias_gestion', valores.pertenencias_gestion)}` : '';
    p.push(`Pertenencias ${opLabel('pertenencias', valores.pertenencias)}${gestion}.`);
  }

  if (visitaCubre.educacion) {
    p.push(...sec.educacion);
  } else if (opLabel('educacion_a', valores.educacion_a) && etiquetas('educacion_tema', valores.educacion_tema).length) {
    p.push(`Se realiza educación a ${opLabel('educacion_a', valores.educacion_a)} sobre ${listaProsa(etiquetas('educacion_tema', valores.educacion_tema))}.`);
    if (valores.educacion_resultado) {
      const r = valores.educacion_resultado;
      p.push(r === 'comprende' ? 'Comprende indicaciones entregadas.' : r === 'no_comprende' ? 'No comprende indicaciones, se reforzará.' : 'Requiere refuerzo de indicaciones entregadas.');
    }
  }

  // De qué se le avisó a la enfermera (campo propio de sala). Es un hecho que la persona
  // marcó: se cuenta aunque el servicio cambie a mitad de turno.
  const informa = etiquetas('informa', valores.informa);
  if (informa.length) p.push(`Se informa a enfermera de ${listaProsa(informa)}.`);

  if (valores.observacion) p.push(`${opLabel('observacion', valores.observacion)}.`);

  return p.join(' ');
}

// Red de seguridad del pulido que es propia del documento del TENS (se suma a
// `verificarPulido`, que no conoce estas frases). El pulido redacta, no decide:
// no puede agregar un aviso a la enfermera que nadie constató, convertir un "por
// informar" en "se informa", ni cambiar el nivel de un riesgo declarado. Devuelve la
// lista de motivos (vacía si todo calza). Falla hacia descartar: ante la duda queda el
// texto determinista.
const RE_AVISO = /\b(?:se\s+)?(?:informa|avisa)(?:r|do|da)?\b[^.;]{0,30}enfermer/gi;
const RE_POR_INFORMAR = /\bpor\s+informar\b/gi;
const RE_RIESGO = /riesgo\s+(alto|moderado|bajo)\s+de\s+(ca[íi]das|lesi[óo]n\s+por\s+presi[óo]n)/gi;
const cuenta = (re, t) => (String(t || '').match(re) || []).length;
const riesgos = (t) => Array.from(String(t || '').matchAll(RE_RIESGO))
  .map((m) => `${m[1].toLowerCase()}|${m[2].toLowerCase().startsWith('ca') ? 'caidas' : 'lpp'}`).sort();

export function verificarPulidoTens(borrador, pulido) {
  const motivos = [];
  const avisosAntes = cuenta(RE_AVISO, borrador);
  const avisosDespues = cuenta(RE_AVISO, pulido);
  if (avisosDespues > avisosAntes) motivos.push('agregó un aviso a la enfermera que no estaba registrado');
  if (avisosAntes > 0 && avisosDespues === 0) motivos.push('quitó los avisos a la enfermera');
  if (cuenta(RE_POR_INFORMAR, pulido) < cuenta(RE_POR_INFORMAR, borrador)) motivos.push('cambió un "por informar" que seguía pendiente');
  if (riesgos(borrador).join(',') !== riesgos(pulido).join(',')) motivos.push('cambió el nivel de un riesgo declarado');
  return motivos;
}

// Agrupación de la evolución para la UI: la plantilla UPCA leída como
// secciones (mismo orden del papel de Tania). El array ES el orden de la
// pantalla y de la prosa; no reordenar sin mirar compilarEvolucionTens.
export const GRUPOS_EVOLUCION_TENS = [
  { id: 'estado', label: 'Estado del paciente', campos: ['condiciones', 'conciencia', 'conducta', 'soporte', 'dva'] },
  { id: 'piel', label: 'Piel', campos: ['piel'] },
  { id: 'dispositivos', label: 'Dispositivos', campos: ['dispositivos', 'sitio_insercion'] },
  { id: 'alimentacion', label: 'Alimentación', campos: ['regimen', 'tolerancia'] },
  { id: 'eliminacion', label: 'Eliminación', campos: ['diuresis', 'deposiciones'] },
  { id: 'seguridad', label: 'Seguridad y pertenencias', campos: ['seguridad', 'pertenencias', 'pertenencias_gestion'] },
  { id: 'educacion', label: 'Educación', campos: ['educacion_a', 'educacion_tema', 'educacion_resultado'] },
  { id: 'observaciones', label: 'Observaciones', campos: ['observacion'] },
];

// ¿Cuántos grupos tienen al menos un dato? Alimenta el progreso de la UI sin
// convertirlo en nota: es orientación, no calificación (anti-culpa).
export function progresoEvolucionTens(valores = {}, grupos = GRUPOS_EVOLUCION_TENS) {
  const conDatos = grupos.filter((g) =>
    g.campos.some((c) => {
      const v = valores[c];
      return Array.isArray(v) ? v.length > 0 : !!v;
    }),
  ).length;
  return { conDatos, total: grupos.length };
}

// ══════════════════════════════════════════════════════════════════════════
// EL DÍA DEL TENS (dictado del fundador, 26-jul-2026, tercera vuelta)
// ══════════════════════════════════════════════════════════════════════════
// El recorrido real, y por eso el orden de la ficha:
//   1. RECIBO: recibo turno, converso con la enfermera a cargo, reviso el
//      kárdex.
//   2. VISITA con la enfermera: examen físico conjunto, con los énfasis que
//      son del TENS (identificación, dispositivos, piel, alimentación,
//      pertenencias, educación). De acá SALE la evolución.
//   3. RONDAS: rotación de CUP y cambios de posición según riesgo de LPP.
//   4. EVOLUCIÓN: se arma de todo lo anterior.
//
// Invariante de la capa: cada ítem de verificación trae su `educa`, el porqué
// clínico en lenguaje simple. La capa TENS enseña mientras se usa.

// ── Riesgo de LPP: lo declara el TENS, la frecuencia la define la enfermera ──
// Hasta el 2026-10-10 este catálogo traía `cadaMin` (120 con riesgo alto, 180 con
// moderado) y la app llevaba un reloj de cambios de posición con "toca ahora". Eran el
// protocolo de una UCI de adultos y se aplicaban a todos los servicios, Bupa incluido.
// El fundador lo sacó para todos ("sacar la ronda de cada 2h"): la frecuencia es una
// decisión de enfermería por paciente (la ficha de cuidados ya lo decía, y la fuente de
// sala da un rango, no una cifra). El TENS sigue registrando cada cambio con su hora; lo
// que se fue es el reloj que lo presionaba. No restaurar un `cadaMin` aquí: el legado de
// la visita de la enfermera guarda sus propios minutos en visitaEnfermeria.js.
export const RIESGO_LPP = [
  {
    id: 'alto', label: 'Alto',
    pauta: 'Cambios de posición según la planificación de tu enfermera.',
    educa: 'Con riesgo alto la piel se daña más rápido en los puntos de apoyo. En cada cambio mira la zona que estuvo apoyada.',
  },
  {
    id: 'moderado', label: 'Moderado',
    pauta: 'Cambios de posición según la planificación de tu enfermera.',
    educa: 'Si ves un enrojecimiento que no cede al cambiar de posición, regístralo y avísale a tu enfermera.',
  },
  {
    id: 'bajo', label: 'Bajo',
    pauta: 'Estimula el cambio de posición.',
    educa: 'En riesgo bajo el paciente se moviliza solo, y tu rol es estimularlo y acompañarlo.',
  },
];

export const riesgoDef = (id) => RIESGO_LPP.find((r) => r.id === id) || null;

// Próximos cuidados por reloj: hoy solo la rotación de CUP cada 2 h, que es un rasgo
// institucional (Bupa). El cambio de posición ya NO se agenda (2026-10-10, ver
// RIESGO_LPP): `riesgo` y `ultimoCambio` se aceptan y se ignoran para no romper a los
// llamadores. Devuelve SOLO lo que aplica al paciente, ordenado por hora.
// Puro y testeable: la UI no calcula horas por su cuenta.
// `rotacionCupMin`: sin perfil que la active llega null y no se agenda. Por defecto
// conserva el valor de siempre (120 min).
export function proximosCuidados({ dispositivos = [], ultimaRotacionCup = null, inicioTurno = null, rotacionCupMin = ROTACION_DISPOSITIVOS_MIN.cup }, ahora = Date.now()) {
  const out = [];
  const base = (ultimo) => ultimo || inicioTurno || null;

  if (rotacionCupMin && dispositivos.includes('cup')) {
    const desde = base(ultimaRotacionCup);
    if (desde) {
      const due = desde + rotacionCupMin * 60000;
      out.push({ id: 'rotacion_cup', label: 'Rotación de CUP', due, vencido: ahora > due, cadaMin: rotacionCupMin });
    }
  }
  return out.sort((a, b) => a.due - b.due);
}

// ── Dispositivos que se verifican en la visita ───────────────────────────────
export const DISPOSITIVOS_VISITA = [
  { id: 'sng', label: 'SNG' },
  { id: 'sny', label: 'SNY' },
  { id: 'cvc', label: 'CVC' },
  { id: 'la', label: 'Línea arterial' },
  { id: 'vvp', label: 'VVP' },
  { id: 'cup', label: 'CUP' },
  { id: 'tet', label: 'TET' },
  { id: 'tqt', label: 'TQT' },
  { id: 'drenajes', label: 'Drenajes' },
];

// Chequeos POR dispositivo. Cada uno con su porqué: el TENS aprende la razón
// clínica mientras verifica, no memoriza una lista.
// `labelPropia`/`educaPropia` (2026-10-10, pedido del fundador): fuera de la visita
// conjunta de Bupa la casilla no dice "con la enfermera", porque allá el técnico visita
// solo. Con la visita conjunta se usa el `label` de siempre (el fixture de UCI no cambia).
export const CHEQUEOS_DISPOSITIVO = {
  sng: [
    { id: 'fijada', label: 'Fijada, sin tracción', educa: 'Una sonda que tracciona lesiona el ala nasal en horas. Revisa que la fijación no apriete.' },
    { id: 'corroborada_eu', label: 'Posición corroborada con la enfermera', labelPropia: 'Posición corroborada por enfermería', educa: 'La posición de la SNG la confirma la enfermera antes de pasar cualquier cosa. Nunca alimentes sin esa confirmación.' },
  ],
  sny: [
    { id: 'fijada', label: 'Fijada, sin tracción', educa: 'Igual que la SNG: la tracción es la causa número uno de lesión nasal por dispositivo.' },
    { id: 'corroborada_eu', label: 'Posición corroborada con la enfermera', labelPropia: 'Posición corroborada por enfermería', educa: 'La confirmación de posición es de la enfermera, siempre antes de alimentar.' },
  ],
  cvc: [
    { id: 'sitio_limpio', label: 'Sitio de inserción limpio y cubierto', educa: 'El apósito del CVC es la barrera contra la infección del torrente. Si está despegado o húmedo, avisa.' },
    { id: 'fijado', label: 'Fijado y sin tracción', educa: 'Un CVC traccionado puede salirse o migrar. La fijación se mira en cada visita.' },
  ],
  la: [
    { id: 'cero_eu', label: 'Cero realizado con la enfermera', labelPropia: 'Cero del transductor realizado', educa: 'Sin hacer cero, la presión que muestra el monitor no es real. El cero se hace con la enfermera al inicio y al cambiar la posición del transductor.', educaPropia: 'Sin hacer cero, la presión que muestra el monitor no es real. El cero se hace al inicio y al cambiar la posición del transductor.' },
    { id: 'apurador_300', label: 'Apurador a 300 mmHg', educa: 'A 300 mmHg el sistema mantiene el lavado continuo que impide que la línea se coagule. Menos presión, línea perdida.' },
    { id: 'mismo_lado', label: 'Apurador del mismo lado, sin cruzar', educa: 'Cruzar líneas sobre la cama es tropiezo, tracción y desconexión. Cada cosa por su lado.' },
    { id: 'sitio_visible', label: 'Sitio visible y sin sangrado', educa: 'La línea arterial sangra rápido si se suelta: el sitio se deja siempre a la vista.' },
  ],
  vvp: [
    { id: 'permeable', label: 'Permeable y fijada', educa: 'Una vía que no pasa o que se movió deja al paciente sin acceso justo cuando se necesita.' },
    { id: 'sitio_sin_signos', label: 'Sitio sin enrojecimiento ni dolor', educa: 'Enrojecimiento, dolor o hinchazón son flebitis o extravasación. Avisa a tu enfermera al tiro.' },
  ],
  cup: [
    { id: 'fijador', label: 'Con su fijador', educa: 'El fijador evita que la sonda tironee la uretra en cada movimiento. Es prevención de lesión, no un detalle.' },
    { id: 'sellado', label: 'Sistema sellado', educa: 'Abrir el circuito es abrirle la puerta a la infección urinaria. El sistema se mantiene cerrado.' },
    { id: 'bolsa_bajo_vejiga', label: 'Bolsa bajo el nivel de la vejiga', educa: 'Si la bolsa queda sobre la vejiga, la orina refluye y con ella las bacterias. Nunca sobre la cama ni colgada arriba.' },
  ],
  tet: [
    { id: 'bajo_fijacion', label: 'Piel bajo la fijación revisada', educa: 'Debajo de la fijación del tubo es donde nadie mira y donde aparece la lesión. Levántala con la enfermera y mira.' },
    { id: 'fijacion_firme', label: 'Fijación firme, sin tracción', educa: 'Un tubo que se mueve puede extubar al paciente. La fijación se comprueba en cada visita.' },
  ],
  tqt: [
    { id: 'sitio_limpio', label: 'Estoma limpio y seco', educa: 'La humedad alrededor del estoma macera la piel y facilita la infección.' },
    { id: 'fijacion_firme', label: 'Fijación firme, sin tracción', educa: 'La cánula se sostiene por su fijación: revísala sin apretar el cuello.' },
  ],
  drenajes: [
    { id: 'permeable', label: 'Permeable y fijado', educa: 'Un drenaje acodado deja de drenar y el contenido se acumula donde no debe.' },
    { id: 'bajo_nivel', label: 'Bajo el nivel del sitio', educa: 'Por gravedad: si el recolector queda arriba, el contenido vuelve.' },
  ],
};

// Chequeos GENERALES del entorno de la cama (van siempre, no dependen del
// dispositivo). El orden importa: es el barrido visual real de la unidad.
export const CHEQUEOS_ENTORNO = [
  { id: 'lineas_ordenadas', label: 'Líneas y cables ordenados, sin cruzar', educa: 'Líneas cruzadas sobre la cama son tracción y caídas. Ordenar es prevención, no estética.' },
  { id: 'apuradores_lado', label: 'Apuradores y bombas del lado que corresponde', educa: 'Cada equipo del lado de su acceso: mover al paciente con las líneas cruzadas termina en desconexión.' },
  { id: 'unidad_ordenada', label: 'Unidad ordenada y alcance libre', educa: 'El timbre y lo que el paciente necesita, siempre a su alcance.' },
];

// ── Piel: los puntos que el TENS mira SIEMPRE en la visita ───────────────────
// Énfasis dictado: sacra, talones, nariz, y debajo de la fijación del tubo.
// De la cabeza a los pies, como se recorre de verdad. El ORDEN DEL ARRAY es
// el orden de la pantalla Y el de la prosa: nunca el orden en que el usuario
// fue tocando (misma doctrina que el examen céfalo-caudal del enfermero).
export const PUNTOS_PIEL_VISITA = [
  { id: 'occipucio', label: 'Occipucio', educa: 'En pacientes sedados la cabeza no se mueve sola y el occipucio se apoya horas.' },
  { id: 'nariz', label: 'Nariz', educa: 'Si hay SNG o naricera, la lesión sale bajo el punto de apoyo del dispositivo.' },
  { id: 'bajo_fijacion_tet', label: 'Bajo la fijación del tubo', educa: 'Solo se ve si levantas la fijación con la enfermera. Es la lesión que más se pasa por alto.', dependeDe: ['tet', 'tqt'] },
  { id: 'sacra', label: 'Zona sacra', educa: 'El punto donde más aparecen lesiones en cama: se mira en cada visita, no solo si duele.' },
  { id: 'talones', label: 'Talones', educa: 'El talón no avisa: la lesión aparece sin dolor previo. Descárgalos con almohada bajo las pantorrillas.' },
  { id: 'otras_prominencias', label: 'Otras prominencias óseas', educa: 'Trocánteres, codos, orejas y rodillas según la posición en que estuvo. Va al final porque recoge lo que no tiene fila propia.' },
];

export const ESTADO_PUNTO_PIEL = [
  { id: 'sin_alteracion', label: 'Sin alteración', reporta: false },
  { id: 'enrojecida', label: 'Enrojecida', reporta: true },
  { id: 'humedad', label: 'Con humedad', reporta: true },
  { id: 'lesion', label: 'Con lesión visible', reporta: true },
];

// ── Alimentación ─────────────────────────────────────────────────────────────
export const VIA_ALIMENTACION = [
  { id: 'sng', label: 'Por SNG o SNY', educa: 'Antes de pasar alimentación por sonda, la posición la confirma la enfermera y el paciente queda semisentado.' },
  { id: 'oral', label: 'Régimen oral', educa: 'Acompaña la alimentación y observa cómo traga: la tos al comer es una alarma que se avisa.' },
  { id: 'ayuno', label: 'En ayuno', educa: 'En ayuno el cuidado bucal es más importante todavía: la boca seca se lesiona.' },
];

export const TOLERANCIA_ALIMENTACION = [
  { id: 'bien', label: 'Bien tolerada', reporta: false },
  { id: 'nauseas', label: 'Con náuseas', reporta: true },
  { id: 'vomitos', label: 'Con vómitos', reporta: true },
  { id: 'residuo', label: 'Con residuo por sonda', reporta: true },
];

// ── Identificación: el brazalete ─────────────────────────────────────────────
export const BRAZALETE_ESTADO = [
  { id: 'correcto', label: 'Correcto y legible', reporta: false },
  { id: 'ilegible', label: 'Ilegible o deteriorado', reporta: true },
  { id: 'ausente', label: 'Ausente', reporta: true },
];

export const BRAZALETE_UBICACION = [
  { id: 'muneca_der', label: 'Muñeca derecha' },
  { id: 'muneca_izq', label: 'Muñeca izquierda' },
  { id: 'tobillo_der', label: 'Tobillo derecho' },
  { id: 'tobillo_izq', label: 'Tobillo izquierdo' },
];

// ── Las etapas de la visita, en el orden real ────────────────────────────────
// El orden de la visita tal como ocurre: te presentas, identificas, revisas
// dispositivos, ves cómo se alimenta y recién ahí lo movilizas para mirarle
// la piel. La piel al final porque es lo que exige mover al paciente.
export const ETAPAS_VISITA = [
  { id: 'presentacion', label: 'Saludo y presentación' },
  { id: 'identificacion', label: 'Identificación' },
  { id: 'dispositivos', label: 'Dispositivos' },
  { id: 'alimentacion', label: 'Alimentación' },
  { id: 'piel', label: 'Piel y prominencias' },
  { id: 'pertenencias', label: 'Pertenencias' },
  { id: 'educacion', label: 'Educación' },
];

// ¿Qué puntos de piel aplican según los dispositivos del paciente?
export function puntosPielAplicables(dispositivos = []) {
  return PUNTOS_PIEL_VISITA.filter(
    (p) => !p.dependeDe || p.dependeDe.some((d) => dispositivos.includes(d)),
  );
}

// Completitud de la visita: cuántas etapas tienen algo. Orienta, no califica.
export function progresoVisita(visita = {}) {
  const tiene = (id) => {
    const v = visita[id];
    if (!v) return false;
    if (typeof v === 'object') return Object.values(v).some((x) => x !== null && x !== undefined);
    return true;
  };
  const conDatos = ETAPAS_VISITA.filter((e) => tiene(e.id)).length;
  return { conDatos, total: ETAPAS_VISITA.length };
}

// Todo lo que la visita pesquisó y hay que avisar a la enfermera.
export function pesquisasDeVisita(visita = {}) {
  const out = [];
  const brz = BRAZALETE_ESTADO.find((b) => b.id === visita.identificacion?.estado);
  const avisadaBrz = visita.identificacion?.avisada;
  if (brz?.reporta) out.push({ tipo: 'identificacion', label: `Brazalete ${brz.label.toLowerCase()}`, ...(typeof avisadaBrz === 'boolean' ? { avisada: avisadaBrz } : {}) });
  const aplicables = puntosPielAplicables(Object.keys(visita.dispositivos || {}));
  for (const [punto, estado] of Object.entries(visita.piel || {})) {
    const e = ESTADO_PUNTO_PIEL.find((x) => x.id === estado);
    if (e?.reporta) {
      // Solo un punto del catálogo que aplique a los dispositivos de hoy (ver seccionesVisitaTens).
      const p = aplicables.find((x) => x.id === punto);
      // Un punto que no es del catálogo (un jsonb adulterado) jamás se imprime.
      if (!p) continue;
      const avisada = visita.pielAvisada?.[punto];
      out.push({ tipo: 'piel', punto, label: `${p.label}: ${e.label.toLowerCase()}`, ...(typeof avisada === 'boolean' ? { avisada } : {}) });
    }
  }
  const tol = TOLERANCIA_ALIMENTACION.find((t) => t.id === visita.alimentacion?.tolerancia);
  if (tol?.reporta) out.push({ tipo: 'alimentacion', label: `Alimentación ${tol.label.toLowerCase()}` });
  return out;
}

// Baja SOLO la inicial para encajar una etiqueta dentro de una oración, sin
// destrozar siglas ni unidades: toLowerCase() convertía "300 mmHg" en
// "300 mmhg" y "SNG" en "sng" (bug cazado por test). Si la primera palabra es
// una sigla en mayúsculas, no se toca nada.
export function bajaInicial(txt) {
  if (!txt) return '';
  const primera = txt.split(/\s+/)[0];
  if (primera.length >= 2 && primera === primera.toUpperCase() && /[A-ZÁÉÍÓÚÑ]/.test(primera)) return txt;
  return txt.charAt(0).toLowerCase() + txt.slice(1);
}

// ── La visita se vuelve prosa: las líneas que entran a la evolución ──────────
// Determinista y del catálogo. Solo lo verificado aparece: nada se afirma sin
// haberlo mirado (misma invariante que el examen del enfermero).
// Secciones de la visita, para que el compilador las intercale en el orden
// exacto de la plantilla UPCA (lo clínico arriba, pertenencias y educación al
// cierre). lineasVisitaTens() sigue devolviendo todo junto: es la API pública.
// `conEnfermera` (por defecto true, como siempre) es la rutina de la UCI de Bupa, donde la visita se
// hace en conjunto: la prosa dice "junto a la enfermera" y que se informa un hallazgo de piel. Donde
// nadie lo constató (cualquier otra institución) esas dos afirmaciones no se escriben: lo que el técnico
// SÍ marcó como avisado (brazalete, sitio de inserción, pesquisas) sigue diciéndolo por su propia casilla.
export function seccionesVisitaTens(visita = {}, { conEnfermera = true } = {}) {
  const junto = conEnfermera ? ' junto a la enfermera' : '';
  const L = [];
  if (visita.presentacion) L.push('Se saluda y presenta al paciente al inicio del turno.');

  const idf = visita.identificacion || {};
  if (idf.estado) {
    const est = BRAZALETE_ESTADO.find((b) => b.id === idf.estado);
    const ubi = BRAZALETE_UBICACION.find((u) => u.id === idf.ubicacion);
    if (idf.estado === 'correcto') {
      L.push(`Brazalete de identificación correcto y legible${ubi ? ` en ${bajaInicial(ubi.label)}` : ''}, verificado${junto}.`);
    } else {
      // Igual que en la ronda: el aviso se constata. Sin la clave (registros
      // viejos) dice lo de siempre; `avisada === false` lo deja por informar.
      L.push(idf.avisada === false
        ? `Brazalete de identificación ${bajaInicial(est.label)}, por informar a enfermera para reposición.`
        : `Brazalete de identificación ${bajaInicial(est.label)}, se informa a enfermera y se solicita reposición.`);
    }
  }

  const disp = visita.dispositivos || {};
  const conDisp = Object.keys(disp).filter((d) => Object.keys(disp[d] || {}).length > 0);
  if (conDisp.length) {
    const partes = conDisp.map((d) => {
      const label = DISPOSITIVOS_VISITA.find((x) => x.id === d)?.label || d;
      const ok = Object.entries(disp[d]).filter(([, v]) => v).map(([k]) => {
        const c = (CHEQUEOS_DISPOSITIVO[d] || []).find((x) => x.id === k);
        return bajaInicial((!conEnfermera && c?.labelPropia) || c?.label || '');
      }).filter(Boolean);
      return ok.length ? `${label} ${ok.join(', ')}` : label;
    });
    L.push(`Se verifican dispositivos${junto}: ${partes.join('; ')}.`);
  }
  const ent = Object.entries(visita.entorno || {}).filter(([, v]) => v).map(([k]) =>
    bajaInicial(CHEQUEOS_ENTORNO.find((c) => c.id === k)?.label || '')).filter(Boolean);
  if (ent.length) L.push(`Entorno de la unidad: ${ent.join(', ')}.`);

  const piel = visita.piel || {};
  // Recorre el CATÁLOGO, no las claves del objeto: el orden de la prosa es
  // céfalo-caudal, no el orden en que el usuario fue tocando los chips.
  // Solo los puntos que aplican a los dispositivos de hoy: si se quitó el TET, el punto
  // "bajo la fijación del tubo" ya no tiene fila en pantalla, y un "por informar" que no
  // se puede cerrar no puede quedar en el documento (auditoría 2026-10-10).
  const aplicables = puntosPielAplicables(Object.keys(visita.dispositivos || {}));
  const revisados = aplicables.map((x) => x.id).filter((id) => piel[id]);
  if (revisados.length) {
    const sin = revisados.filter((p) => piel[p] === 'sin_alteracion');
    const con = revisados.filter((p) => piel[p] && piel[p] !== 'sin_alteracion');
    if (sin.length) {
      const nombres = sin.map((p) => bajaInicial(PUNTOS_PIEL_VISITA.find((x) => x.id === p)?.label || p));
      L.push(`Se revisan prominencias óseas${junto}: ${listaProsa(nombres)} sin alteraciones a la vista.`);
    }
    // El aviso se constata, igual que el brazalete (2026-10-10): `pielAvisada[p] === false`
    // dice "por informar", `true` dice "se informa". Sin la clave (registros de antes) la
    // prosa es la de siempre: en la visita conjunta "se informa" y en la propia nada, así
    // el fixture de UCI y lo ya guardado no cambian.
    const avisos = visita.pielAvisada || {};
    for (const p of con) {
      const punto = bajaInicial(PUNTOS_PIEL_VISITA.find((x) => x.id === p)?.label || p);
      const est = bajaInicial(ESTADO_PUNTO_PIEL.find((x) => x.id === piel[p])?.label || '');
      const aviso = avisos[p] === false ? ', por informar a enfermera'
        : avisos[p] === true || conEnfermera ? ', se informa a enfermera' : '';
      L.push(`${punto.charAt(0).toUpperCase() + punto.slice(1)} ${est}${aviso}.`);
    }
  }

  const A = [];
  const P = [];
  const E = [];
  const ali = visita.alimentacion || {};
  if (ali.via) {
    const via = VIA_ALIMENTACION.find((v) => v.id === ali.via);
    const tol = TOLERANCIA_ALIMENTACION.find((t) => t.id === ali.tolerancia);
    if (ali.via === 'ayuno') {
      A.push('Paciente en ayuno según indicación, se mantienen cuidados bucales.');
    } else {
      A.push(`Alimentación ${bajaInicial(via.label)}${tol ? `, ${bajaInicial(tol.label)}` : ''}. Se asiste según indicación y tolerancia.`);
    }
  }
  // ── Cierre de la plantilla: pertenencias y educación ──────────────────────
  const per = visita.pertenencias || {};
  if (per.pertenencias) {
    const est = EVOLUCION_TENS.find((c) => c.id === 'pertenencias')?.opciones.find((o) => o.id === per.pertenencias);
    const ges = EVOLUCION_TENS.find((c) => c.id === 'pertenencias_gestion')?.opciones.find((o) => o.id === per.pertenencias_gestion);
    P.push(`Pertenencias ${est?.label || per.pertenencias}${ges ? `, ${ges.label}` : ''}, verificadas junto al paciente.`);
  }

  const edu = visita.educacion || {};
  if (edu.educacion_a && (edu.educacion_tema || []).length) {
    const a = EVOLUCION_TENS.find((c) => c.id === 'educacion_a')?.opciones.find((o) => o.id === edu.educacion_a);
    // Los temas propios de sala (prevención de caídas, alimentación) también se resuelven.
    const temas = (edu.educacion_tema || []).map((t) => opLabelEn(EVOLUCION_TENS, 'educacion_tema', t)).filter(Boolean);
    if (temas.length) {
      E.push(`Se realiza educación a ${a?.label || edu.educacion_a} sobre ${listaProsa(temas)}.`);
      if (edu.educacion_resultado) {
        const r = edu.educacion_resultado;
        E.push(r === 'comprende' ? 'Comprende indicaciones entregadas.'
          : r === 'no_comprende' ? 'No comprende indicaciones, se reforzará.'
          : 'Requiere refuerzo de indicaciones entregadas.');
      }
    }
  }

  return { clinicas: L, alimentacion: A, pertenencias: P, educacion: E };
}

// API pública: todas las líneas de la visita, en orden de plantilla.
export function lineasVisitaTens(visita = {}, opciones = {}) {
  const s = seccionesVisitaTens(visita, opciones);
  return [...s.clinicas, ...s.alimentacion, ...s.pertenencias, ...s.educacion];
}

// ── Recibo del turno (primera fase del día, por paciente) ────────────────────
export const RECEPCION_TENS = [
  { id: 'recibo_turno', label: 'Recibo el turno del TENS saliente', educa: 'Lo que te entregan es la base: qué pasó en la noche, qué quedó pendiente y qué hay que vigilar.' },
  { id: 'converso_enfermera', label: 'Converso con la enfermera a cargo', educa: 'Ella te dice el foco del paciente hoy. Preguntar temprano evita repetir trabajo y descubrir cosas tarde.' },
  { id: 'reviso_kardex', label: 'Reviso indicaciones en el kárdex', educa: 'El kárdex manda: régimen, reposo, cuidados indicados y frecuencias. Míralo antes de la visita, no después.' },
];

// ── Fases del día del TENS: el orden de la ficha ES el del recorrido real ────
export const FASES_TENS = [
  { id: 'recibo', label: 'Recibir' },
  { id: 'visita', label: 'Visitar' },
  { id: 'rondas', label: 'Rondas' },
  { id: 'evolucion', label: 'Evolucionar' },
];
